import Switch from '../../../components/form/Switch'
import PopupSection from '../../../components/popup/PopupSection'
import PopupSectionRow from '../../../components/popup/PopupSectionRow'
import { ChangeEvent, useState } from 'react'
import { updateGroupSettings } from '../../../services/api/group'
import Logger from '../../../services/Logger'
import { GroupDoc, GroupSettings } from '../../../types/api/changes'
import { Model } from '../../../types/model'
import {
  featureToMinimumTierNeeded,
  isUserAdminInGroup,
  TierFeatures
} from 'utils/api/groups'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { GENERIC_ERROR } from 'utils/strings'
import { GroupPrivacy } from 'model/models/Group'
import { useConnection } from 'components/providers/ConnectionProvider'
import { t } from 'i18next'
import { Tier } from 'utils/api/accounts'
import { parseErrorToMessage } from '../eventInfo/EventPopup'
import { Box } from '@mui/material'

interface MembersCanProps {
  group: Model<GroupDoc>
  setLoading: (b: boolean) => void
  error: string
  setError: (error: string) => void
  groupMetaDataCheck?: (event: ChangeEvent<HTMLInputElement>) => void
  allCanAddItemsCheck?: (event: ChangeEvent<HTMLInputElement>) => void
  addParticipantsCheck?: (event: ChangeEvent<HTMLInputElement>) => void
  eventOwnershipCheck?: (event: ChangeEvent<HTMLInputElement>) => void
  metaDataState?: string
  allCanAddItemsState?: string
  addParticipantsState?: string
  eventOwnershipState?: string
}

export default function MemberCanSection(props: MembersCanProps) {
  const connection = useConnection()

  async function updateSettings(data: GroupSettings, group: Model<GroupDoc>) {
    if (connection.isConnectionAvailable()) {
      props.setLoading(true)
      try {
        await updateGroupSettings(data, group)
      } catch (e) {
        Logger.red(String(e))
        props.setError(parseErrorToMessage(e))
      }
      props.setLoading(false)
    }
  }

  const changeGroupMetaData = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.groupMetaDataCheck) {
      props.groupMetaDataCheck(event)
      return
    }

    updateSettings(
      {
        ...props.group.GroupSettings,
        IsAllParticipantsCanEditGroupMetadata: event.target.checked ? '1' : '0'
      },
      props.group
    )
  }

  const changeAllCanAddItems = async (event: ChangeEvent<HTMLInputElement>) => {
    if (props.allCanAddItemsCheck) {
      props.allCanAddItemsCheck(event)
      return
    }

    const newValue = event.target.checked ? '1' : '0'

    updateSettings(
      {
        ...props.group.groupSettings,
        IsAllParticipantsCanAddItems: newValue,
        IsAllParticipantsCanEditOnlyTheirEvents:
          newValue === '0' &&
          props.group.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents ===
            '1'
            ? '0'
            : undefined
      },
      props.group
    )
  }

  const changeAddParticipants = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (props.addParticipantsCheck) {
      props.addParticipantsCheck(event)
      return
    }

    updateSettings(
      {
        ...props.group.groupSettings,
        IsAllParticipantsCanAddParticipants: event.target.checked ? '1' : '0'
      },
      props.group
    )
  }
  const changeEventOwnership = async (event: ChangeEvent<HTMLInputElement>) => {
    if (props.eventOwnershipCheck) {
      props.eventOwnershipCheck(event)
      return
    }

    updateSettings(
      {
        ...props.group.groupSettings,
        IsAllParticipantsCanEditOnlyTheirEvents: event.target.checked
          ? '1'
          : '0',
        IsAllParticipantsCanAddItems:
          event.target.checked &&
          (!props.group.GroupSettings?.IsAllParticipantsCanAddItems ||
            props.group.GroupSettings?.IsAllParticipantsCanAddItems === '0')
            ? '1'
            : undefined
      },
      props.group
    )
  }

  return props.group._id?.length === 0 ||
    (isUserAdminInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ) &&
      props.group.PrivacyMode === GroupPrivacy.PRIVATE) ? (
    <>
      <PopupSection title={`${t('membersCan')}`}>
        <PopupSectionRow
          title={`${t('addEditEvents')}`}
          tierFeature={TierFeatures.PERMISSIONS_EVENTS}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_EVENTS)
          }
          value={
            <Switch
              onChange={changeAllCanAddItems}
              checked={
                (props.allCanAddItemsState ??
                  props.group.GroupSettings?.IsAllParticipantsCanAddItems) ===
                '1'
              }
            />
          }
        />
        <Box marginLeft={'16px'}>
          <PopupSectionRow
            title={`${t('groupSettingsEditOnlyOwners')}`}
            tierFeature={TierFeatures.EDIT_THEIR_EVENTS_ONLY}
            neededTier={
              props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
                ? Tier.FREE
                : featureToMinimumTierNeeded(
                    TierFeatures.EDIT_THEIR_EVENTS_ONLY
                  )
            }
            value={
              <Switch
                onChange={changeEventOwnership}
                checked={
                  (props.eventOwnershipState ??
                    props.group.GroupSettings
                      ?.IsAllParticipantsCanEditOnlyTheirEvents) === '1'
                }
              />
            }
          />
        </Box>
        <PopupSectionRow
          title={`${t('editCalendarName')}`}
          tierFeature={TierFeatures.PERMISSIONS_CALENDAR_NAME}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(
                  TierFeatures.PERMISSIONS_CALENDAR_NAME
                )
          }
          value={
            <Switch
              checked={
                (props.metaDataState ??
                  props.group.GroupSettings
                    ?.IsAllParticipantsCanEditGroupMetadata) === '1'
              }
              onChange={changeGroupMetaData}
            />
          }
        />
        <PopupSectionRow
          title={`${t('addNewMembers')}`}
          tierFeature={TierFeatures.PERMISSIONS_MEMBERS}
          neededTier={
            props.group.OwnerID !== LocalStorage.get(LocalStorageKey.USER_ID)
              ? Tier.FREE
              : featureToMinimumTierNeeded(TierFeatures.PERMISSIONS_MEMBERS)
          }
          value={
            <Switch
              onChange={changeAddParticipants}
              checked={
                (props.addParticipantsState ??
                  props.group.GroupSettings
                    ?.IsAllParticipantsCanAddParticipants) === '1'
              }
            />
          }
        />
      </PopupSection>
    </>
  ) : (
    <></>
  )
}
